import { currencyCodeSuffix } from '../../utils/money';
import { useBrandLocale } from '../../hooks/useBrandLocale';
import { Circle, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import {
  Badge,
  Card,
  CardBody,
  InlineRow,
  LoadingBlock,
  Muted,
  Stack,
  Strong,
} from '../../components/ui';
import type {
  IPublicWorkerFieldsFragment,
  IServiceQuoteFieldsFragment,
} from '../../services/serviceService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import { formatMoney } from '../ServiceProviderPage/priceTables';
import { holdText, onTimeLabel, rangeView } from './serviceJob';
import type { Bounds } from './serviceSearchState';
import { awayText, radiusMeters } from '../../utils/serviceUnits';

/** Pieces the services guest pages share (spec 019 W11, W12, W15). */

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places'];

/** Google Maps takes plain colors: read the brand color from its CSS variable. */
function brandColor(): string {
  if (typeof window === 'undefined') return '#222222';
  const value = getComputedStyle(document.documentElement).getPropertyValue('--color-black').trim();
  return value || '#222222';
}

export type PublicWorker = IPublicWorkerFieldsFragment;
export type ServiceQuote = IServiceQuoteFieldsFragment;

export function WorkerBadges({ worker }: { worker: PublicWorker }) {
  const onTime = onTimeLabel(worker.onTimeRate);
  return (
    <InlineRow style={{ flexWrap: 'wrap' }}>
      {worker.provider.insuranceVerified && (
        <Badge tone="success" size="sm">
          Insured
        </Badge>
      )}
      {worker.backgroundCheckVerified && (
        <Badge tone="success" size="sm">
          Background check
        </Badge>
      )}
      {worker.instantBook && (
        <Badge tone="info" size="sm">
          Instant book
        </Badge>
      )}
      {onTime && (
        <Badge tone="neutral" size="sm">
          {onTime}
        </Badge>
      )}
      {worker.averageRating ? (
        <Badge tone="neutral" size="sm">
          ★ {worker.averageRating.toFixed(1)} ({worker.totalRatings})
        </Badge>
      ) : null}
    </InlineRow>
  );
}

const CardRoot = styled.div`
  ${tw`grid gap-4`}
  grid-template-columns: 120px 1fr;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Photo = styled.div`
  ${tw`overflow-hidden`}
  aspect-ratio: 1;
  border-radius: var(--radius-card, 12px);
  background: var(--color-bg-secondary);
  img {
    ${tw`object-cover w-full h-full`}
  }
`;

const Price = styled.div`
  ${tw`text-lg font-semibold`}
  color: var(--color-text-primary);
`;

export interface WorkerResult {
  worker: PublicWorker;
  car: { id: string; thumbnailUrl?: string | null; images?: { imageUrl?: string | null }[] | null };
  distanceMiles: number;
  rangeStatus: Parameters<typeof rangeView>[0]['rangeStatus'];
  travelFee?: number | null;
  bookable: boolean;
  notBookableReason?: string | null;
  quote?: ServiceQuote | null;
  suppliesWarnings: string[];
}

/**
 * A worker in the results (W11): range status (in the area, outside with a
 * travel fee, or not served with the server's reason and no Book button),
 * badges, on-time rate and the price for this search.
 */
export function WorkerCard({
  result,
  onOpen,
  onBook,
  onHover,
}: {
  result: WorkerResult;
  onOpen: () => void;
  onBook: () => void;
  onHover?: (hovering: boolean) => void;
}) {
  const { worker } = result;
  const range = rangeView(result);
  const photo =
    worker.equipmentPhotos[0] ?? result.car.images?.[0]?.imageUrl ?? result.car.thumbnailUrl;
  return (
    <Card
      $interactive
      data-testid={`worker-result-${worker.carId}`}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <CardBody>
        <CardRoot>
          <Photo onClick={onOpen} role="button" aria-label={`Open ${worker.displayName}`}>
            {photo && <img src={toPublicS3ObjectUrl(photo)} alt="" loading="lazy" />}
          </Photo>
          <Stack $gap={4}>
            <div>
              <Strong>{worker.displayName}</Strong>
              <div>
                <Muted $size="sm">
                  {worker.provider.businessName} · {worker.city} {worker.zip} ·{' '}
                  {awayText(result.distanceMiles, worker.distanceUnit)}
                </Muted>
              </div>
            </div>
            <WorkerBadges worker={worker} />
            <Badge tone={range.tone} data-testid="worker-range">
              {range.label}
            </Badge>
            {result.suppliesWarnings.length > 0 && (
              <Muted $size="sm">{result.suppliesWarnings[0]}</Muted>
            )}
            <InlineRow style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {result.quote ? (
                <Price>
                  {formatMoney(result.quote.total)}
                  {result.quote.isEstimate ? ' (up to)' : ''}
                </Price>
              ) : (
                <span />
              )}
              <InlineRow>
                <Button type="button" theme="secondary" size="sm" text="View" onClick={onOpen} />
                <Button
                  type="button"
                  size="sm"
                  text="Book"
                  isDisabled={!result.bookable}
                  onClick={onBook}
                  dataTestId={`worker-book-${worker.carId}`}
                />
              </InlineRow>
            </InlineRow>
          </Stack>
        </CardRoot>
      </CardBody>
    </Card>
  );
}

const Line = styled.div<{ $strong?: boolean }>`
  ${tw`flex justify-between gap-4 text-sm`}
  color: var(--color-text-primary);
  font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
`;

/** The price lines of a quote or a receipt, with the hold (D31). */
export function QuoteBreakdown({
  quote,
  handoverCapHours,
}: {
  quote: ServiceQuote;
  /** D8: a handover bills the time it takes, up to this many hours. */
  handoverCapHours?: number | null;
}) {
  // Spec 028 D14: a quote is in the brand's currency; CAD totals carry the code.
  const { currency } = useBrandLocale();
  return (
    <Stack $gap={4} data-testid="service-quote">
      {quote.lines.map((line, index) => (
        <Line key={`${line.label}-${index}`}>
          <span>{line.label}</span>
          <span>{formatMoney(line.amount)}</span>
        </Line>
      ))}
      <Line $strong>
        <span>{quote.isEstimate ? 'Total, at most' : 'Total'}</span>
        <span>
          {formatMoney(quote.total)}
          {currencyCodeSuffix(currency)}
        </span>
      </Line>
      {quote.isEstimate && handoverCapHours ? (
        <Muted $size="sm">
          The handover is billed for the time it actually takes, up to {handoverCapHours}{' '}
          {handoverCapHours === 1 ? 'hour' : 'hours'}.
        </Muted>
      ) : null}
      <Muted $size="sm" data-testid="service-hold">
        {holdText(quote)}
      </Muted>
    </Stack>
  );
}

const AreaButton = styled.div`
  ${tw`absolute z-10`}
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
`;

export interface MapWorker {
  id: string;
  lat: number;
  lng: number;
  /** In `distanceUnit` (the worker's; spec 028). */
  radiusMiles: number;
  distanceUnit?: string | null;
}

/**
 * Workers on a map (W11, W12): each one's service area drawn as a circle on
 * its ZIP centre, never on the location. "Search this area" sends the box.
 */
export function WorkersMap({
  workers,
  center,
  hoveredId,
  onSearchArea,
  onOpen,
  height = '100%',
}: {
  workers: MapWorker[];
  center?: { lat: number; lng: number };
  hoveredId?: string | null;
  onSearchArea?: (bounds: Bounds) => void;
  onOpen?: (id: string) => void;
  height?: string | number;
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  const [moved, setMoved] = useState(false);
  if (loadError) return <Muted $size="sm">The map could not load.</Muted>;
  if (!isLoaded) return <LoadingBlock label="Loading map" minHeight="240px" />;
  const color = brandColor();
  const fallback = workers[0]
    ? { lat: workers[0].lat, lng: workers[0].lng }
    : { lat: 33.45, lng: -112.07 };
  return (
    <div style={{ position: 'relative', width: '100%', height }} data-testid="workers-map">
      {moved && onSearchArea && (
        <AreaButton>
          <Button
            type="button"
            theme="secondary"
            size="sm"
            text="Search this area"
            onClick={() => {
              const box = mapRef.current?.getBounds();
              if (!box) return;
              const ne = box.getNorthEast();
              const sw = box.getSouthWest();
              onSearchArea({ north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() });
              setMoved(false);
            }}
          />
        </AreaButton>
      )}
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center ?? fallback}
        zoom={10}
        onLoad={map => {
          mapRef.current = map;
        }}
        onDragEnd={() => setMoved(true)}
        onZoomChanged={() => mapRef.current && setMoved(true)}
        options={{ disableDefaultUI: true, zoomControl: true, clickableIcons: false }}
      >
        {workers.map(w => (
          <Circle
            key={`circle-${w.id}`}
            center={{ lat: w.lat, lng: w.lng }}
            radius={radiusMeters(w.radiusMiles, w.distanceUnit)}
            options={{
              fillColor: color,
              fillOpacity: hoveredId === w.id ? 0.18 : 0.06,
              strokeColor: color,
              strokeOpacity: hoveredId === w.id ? 0.7 : 0.3,
              strokeWeight: 1,
              clickable: false,
            }}
          />
        ))}
        {workers.map(w => (
          <Marker key={w.id} position={{ lat: w.lat, lng: w.lng }} onClick={() => onOpen?.(w.id)} />
        ))}
      </GoogleMap>
    </div>
  );
}
